import React, { useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';

/**
 * Solo il riquadro dove si traccia la firma (stesso disegno di SignaturePad),
 * riusabile per ogni passo della firma a più passi. PNG trasparente.
 */
export function SignatureCanvas({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#37352F';
  }, []);

  const point = (e: React.MouseEvent | React.TouchEvent) => {
    const rect = ref.current!.getBoundingClientRect();
    const p = 'touches' in e ? e.touches[0] : (e as React.MouseEvent);
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    setHasSignature(true);
    onChange(ref.current!.toDataURL('image/png'));
  };
  const clear = () => {
    const c = ref.current;
    c?.getContext('2d')?.clearRect(0, 0, c.width, c.height);
    setHasSignature(false);
    onChange(null);
  };

  return (
    <div className="relative bg-[#FBFBFA] border border-dashed border-black/[0.15] rounded-xl overflow-hidden" style={{ touchAction: 'none' }}>
      <canvas
        ref={ref}
        className="w-full h-40 cursor-crosshair"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {!hasSignature && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-sm text-[#9D9892]">Traccia qui la tua firma</span>
        </div>
      )}
      <button
        type="button"
        onClick={clear}
        disabled={!hasSignature}
        className="absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-[#6B6762] bg-white/90 rounded-lg border border-black/[0.08] shadow-sm disabled:opacity-30"
      >
        <Eraser className="h-3.5 w-3.5" />
        Cancella
      </button>
    </div>
  );
}
